import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, map, Observable, startWith, Subscription } from 'rxjs';
import { Ndg } from 'src/app/model/ndg';
import { SurveyForList } from 'src/app/model/survey-response';
import { AuthService } from 'src/app/service/auth.service';
import { MessageService } from 'src/app/service/message.service';
import { NdgService } from 'src/app/service/ndg.service';
import { SpinnerService } from 'src/app/shared/overlay-spinner/service/new-spinner.service';
import { SurveyService } from './service/survey.service';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss']
})
export class SurveyComponent implements OnInit, OnDestroy {
  ndgFormControl = new FormControl();
  ndgList: Ndg[] = [];
  filteredOptions!: Observable<Ndg[]>;
  selectedNdg: Ndg | undefined;

  surveyList: SurveyForList[] = [];
  $surveySubscriber!: Subscription;

  constructor(
    private ndgService: NdgService,
    private surveyService: SurveyService,
    private spinner: SpinnerService,
    private elementRef: ElementRef,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private authService: AuthService
  ) {
    const ndgParam = this.route.snapshot.paramMap.get('ndg');
    this.spinner.showProgress(this.elementRef);

    this.ndgService.getNdgList().subscribe({
      next: (response: Ndg[]) => {
        this.ndgList = response;
        this.spinner.detach(this.elementRef);

        this.filteredOptions = this.ndgFormControl.valueChanges.pipe(
          startWith(undefined),
          map(value => typeof value === 'string' ? value : null),
          map(value => this._filter(value)),
          map(list => list.slice(0, 20))
        );

        if(ndgParam !== 'search') {
          this.selectedNdg = this.ndgList.find(ndg => ndg.ndg === ndgParam);

          if(this.selectedNdg) {
            this.ndgFormControl.setValue(this.selectedNdg);
            this.loadSurveyList(this.selectedNdg);
          } else {
            //TODO ndg not found
            console.error('ndg not found');
            this.router.navigate(['/survey/show-anag']);
          }
        }
      },
      error: () => {
        this.messageService.error('message.error');
        this.spinner.detach(this.elementRef);
      }
    });
  }

  ngOnInit(): void {
    this.$surveySubscriber = this.surveyService.$newSurvey.subscribe({
      next: (newSurvey: SurveyForList) => {
        this.surveyList.unshift(newSurvey);
      }
    });
  }

  ngOnDestroy() {
    this.$surveySubscriber.unsubscribe();
  }

  private _filter(value: string | null): Ndg[] {
    if(!!value) {
      return this.ndgList.filter(
        ndg => ndg.fullName?.toLowerCase().indexOf(value.toLowerCase()) > -1
            || ndg.ndg.toLowerCase().indexOf(value.toLowerCase()) > -1
      );
    }

    return this.ndgList;
  }

  hasInsertPermission(): boolean {
    return this.authService.hasAuthority('USER_INSERT');
  }

  displayNdg(ndg: Ndg): string {
    return ndg ? ndg.fullName + ' (' + ndg.ndg + ')' : '';
  }

  onNdgSelection(ndg: Ndg): void {
    this.router.navigate(['/survey', ndg.ndg]);
    this.selectedNdg = ndg;
    this.loadSurveyList(ndg);
  }

  private loadSurveyList(ndg: Ndg): void {
    this.spinner.showProgress(this.elementRef);

    this.ndgService.getSurveyList(ndg.ndg).subscribe({
      next: (surveyList: SurveyForList[]) => {
        this.ndgService.activeNdg = ndg;
        this.surveyList = surveyList;

        this.spinner.detach(this.elementRef);
      },
      error: () => {
        this.spinner.detach(this.elementRef);
      }
    });
  }

  isSurveySelected(surveyId: string): boolean {
    return this.router.url.endsWith(surveyId);
  }
}
